import { useMemo, useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getOtherReqDetails } from '../../../../store/slices/Requestor/createNewRequestSlice'
import { getTimeOffTypeLabel } from '../../../../utils/timeOffTypes'
import { RootState, AppDispatch } from '../../../../store/store'
import {
  toPerRequestBreakdownEntries,
  type OtherRequestBreakdownEntry,
} from './constants'

interface TooltipEntry {
  durationLabel: string
  date: string
  allocations: Array<{ typeLabel: string; hours: number }>
  totalHours: number
  status?: string
  startTime?: string
  endTime?: string
  hasMultipleRequests?: boolean
  individualRequests?: Array<{
    startTime: string
    endTime: string
    allocations: Array<{ typeLabel: string; hours: number }>
    totalHours: number
  }>
    otherRequests?: any[]
}

interface OtherRequestDetailsResult {
  otherRequestDates: string[]
  otherRequestDetailsForTooltip: Record<string, TooltipEntry>
  /** Per-date breakdown for the "Hours in Other Requests" column, e.g. "3.00 Oth / 2.00 FPD". Keyed by MM/DD/YYYY. */
  otherRequestHoursByDate: Record<string, OtherRequestBreakdownEntry[]>
  handleMonthYearChange: (month: number, year: number) => void
}

/**
 * Hook to fetch and transform other request details for the calendar
 * @param highlightedDates - Array of highlighted dates in MM/DD/YYYY format
 * @param includeStatus - Whether to include status field for on-hold highlighting (default: false)
 * @returns Object containing other request dates, details, and month/year handler
 */

/**
 * Extract individual request from a detail object
 */
const buildDetailRequest = (detail: any, isBirthdayRequest: boolean): any => {
  const detailAllocations = (detail.timeOffAllocations || [])
    .filter((alloc: any) => alloc?.toTypeCd)
    .map((alloc: any) => ({
      typeLabel: getTimeOffTypeLabel(alloc.toTypeCd),
      hours: isBirthdayRequest ? 8 : Number(alloc.hours) || 0,
    }))

  const detailTotalHours = isBirthdayRequest
    ? 8
    : detailAllocations.reduce(
        (sum: number, alloc: any) => sum + alloc.hours,
        0
      )

  return {
    startTime: detail.startTime || '',
    endTime: detail.endTime || '',
    allocations: detailAllocations,
    totalHours: detailTotalHours,
  }
}


/**
 * Build individual requests array based on details or allocations
 */
const buildIndividualRequests = (
  detailsArray: any[],
  isBirthdayRequest: boolean
): any[] => {
  return detailsArray.map((detail) =>
    buildDetailRequest(detail, isBirthdayRequest)
  )
}

/**
 * Check if request has multiple partial day requests
 */
const hasMultiplePartialDayRequests = (
  durationLabel: string,
  detailsArray: any[]
): boolean => {
  return durationLabel === 'Partial Day' && detailsArray.length > 1
}

/**
 * Process a single date entry and return tooltip entry
 */
const processDateEntry = (
  dateKey: string,
  detailsArray: any[],
  includeStatus: boolean
): TooltipEntry | null => {
  if (!Array.isArray(detailsArray) || detailsArray.length === 0) {
    return null
  }

  const parts = dateKey.split('-')
  if (parts.length !== 3) return null

  const [year, month, day] = parts
  const formattedDate = `${month}/${day}/${year}`

  const isBirthdayRequest = detailsArray.some((detail: any) =>
    detail?.timeOffAllocations?.some((alloc: any) => alloc.toTypeCd === 'B')
  )

  const firstDetail = detailsArray[0]
  if (!firstDetail?.timeOffAllocations?.length) {
    return null
  }

  const durationLabel =
    firstDetail.durationFlag === 'F' ? 'Full Day' : 'Partial Day'

const allocationMap: Record<string, number> = {}

detailsArray.forEach((detail: any) => {
  (detail.timeOffAllocations || [])
    .filter((alloc: any) => alloc?.toTypeCd)
    .forEach((alloc: any) => {
      const typeLabel = getTimeOffTypeLabel(alloc.toTypeCd)
      const hours =
        alloc.toTypeCd === 'B'
          ? 8
          : Number(alloc.hours) || 0

      allocationMap[typeLabel] =
        (allocationMap[typeLabel] || 0) + hours
    })
})

const tooltipAllocations = Object.entries(allocationMap).map(
  ([typeLabel, hours]) => ({
    typeLabel,
    hours,
  })
)

const tooltipTotalHours = tooltipAllocations.reduce(
  (sum, alloc) => sum + alloc.hours,
  0
)

  const tooltipEntry: TooltipEntry = {
    durationLabel,
    date: formattedDate,
    allocations: tooltipAllocations,
    totalHours: tooltipTotalHours,
  }
tooltipEntry.otherRequests = detailsArray.flatMap(
  (detail: any) => detail.otherRequests ?? []
)
  if (includeStatus && firstDetail.status) {
    tooltipEntry.status = firstDetail.status
  }

  if (firstDetail.startTime) tooltipEntry.startTime = firstDetail.startTime
  if (firstDetail.endTime) tooltipEntry.endTime = firstDetail.endTime

  if (hasMultiplePartialDayRequests(durationLabel, detailsArray)) {
    tooltipEntry.hasMultipleRequests = true
    tooltipEntry.individualRequests = buildIndividualRequests(
      detailsArray,
      isBirthdayRequest
    )
  }

  return tooltipEntry
}

export const useOtherRequestDetails = (
  highlightedDates: string[],
  includeStatus: boolean = false,
  /**
   * The request currently being edited/resubmitted — excluded from its own
   * "Hours in Other Requests" breakdown. The API's other-requests-for-month
   * data includes every request on a date, including the one being edited,
   * so without this a request's own hours are counted as "other" hours
   * against itself.
   */
  excludeRequestId?: string | number | null
): OtherRequestDetailsResult => {
  const dispatch = useDispatch<AppDispatch>()
  const otherReqData = useSelector(
    (state: RootState) => state.otherReqDetails.data
  )

  // Use ref to track if we've already initialized on mount (prevents double-fetch)
  const hasInitializedRef = useRef(false)

  /**
   * Derive month/year as primitive numbers from the first highlighted date.
   * Using useMemo with primitives as output means the downstream effects only
   * re-fire when the extracted values actually change — not on every render
   * where the highlightedDates array gets a new reference with the same content.
   * This prevents spurious API re-fetches (and the resulting color flicker) each
   * time the parent re-renders with an equivalent dates array.
   */
  const { parsedMonth, parsedYear } = useMemo(() => {
    if (!highlightedDates?.length)
      return {
        parsedMonth: null as number | null,
        parsedYear: null as number | null,
      }
    const parts = highlightedDates[0].split('/')
    if (parts.length !== 3) return { parsedMonth: null, parsedYear: null }
    const m = Number.parseInt(parts[0], 10)
    const y = Number.parseInt(parts[2], 10)
    if (
      Number.isNaN(m) ||
      Number.isNaN(y) ||
      m < 1 ||
      m > 12 ||
      y < 1900 ||
      y > 2100
    ) {
      return { parsedMonth: null, parsedYear: null }
    }
    return { parsedMonth: m - 1, parsedYear: y } // 0-based month
  }, [highlightedDates])

  // Track viewed month/year; initialise from parsed dates so the correct month
  // is already in state before the first effect fires.
  const [viewedMonth, setViewedMonth] = useState(
    () => parsedMonth ?? new Date().getMonth()
  )
  const [viewedYear, setViewedYear] = useState(
    () => parsedYear ?? new Date().getFullYear()
  )

  // Sync viewedMonth/viewedYear whenever the date-derived values arrive or change.
  // Because parsedMonth/parsedYear are primitives this effect only runs when they
  // genuinely change — not on incidental array reference changes in the parent.
  useEffect(() => {
    if (parsedMonth !== null && parsedYear !== null) {
      setViewedMonth(parsedMonth)
      setViewedYear(parsedYear)
    }
  }, [parsedMonth, parsedYear])

  /**
   * Content-based key for the currently highlighted dates. React Router does not
   * remount this component tree when only the `:id` route param changes (e.g.
   * navigating between two different requests' status pages) — so navigating to
   * a different request whose dates fall in the same month/year as the one just
   * viewed would otherwise leave viewedMonth/viewedYear unchanged and skip the
   * refetch below entirely, silently reusing the previous request's now-stale
   * "other requests" data (including hours from a request that has since been
   * withdrawn or denied). Joining to a string (rather than depending on the
   * array itself) still avoids refiring on every render where the parent passes
   * an equivalent array with a new reference.
   */
  const highlightedDatesKey = highlightedDates?.join(',') ?? ''

  // Fetch other request details whenever the viewed month/year changes (initial
  // render, date-derived sync above, user navigating calendar months) or the
  // specific set of highlighted dates changes (navigating to a different request).
  useEffect(() => {
    try {
      if (!hasInitializedRef.current) {
        hasInitializedRef.current = true
      }
      dispatch(getOtherReqDetails({ month: viewedMonth, year: viewedYear }))
    } catch (error) {
      console.error('Error fetching other request details:', error)
    }
  }, [viewedMonth, viewedYear, highlightedDatesKey, dispatch])

  /**
   * Strips out this draft/request's own entries (matched by `toReqId`) from a
   * date's detail array. The month's "other requests" API returns every
   * request on a date, including the one currently being edited — without
   * this, a date that the user just removed client-side (so it dropped out
   * of `highlightedDates`/`editModeSelectedDates`) still matches here as
   * belonging to "another" request. That flips it into the other-request
   * (green, blocked) calendar styling and makes it unclickable, even though
   * it's actually this same still-unsaved draft.
   */
  const excludeOwnRequest = (detailsArray: any[]): any[] => {
    if (
      excludeRequestId === undefined ||
      excludeRequestId === null ||
      excludeRequestId === ''
    ) {
      return detailsArray
    }
    return (detailsArray ?? []).filter(
      (detail) => String(detail?.toReqId ?? '') !== String(excludeRequestId)
    )
  }

  // Convert YYYY-MM-DD keys from API → MM/DD/YYYY for the Datepicker
  const otherRequestDates = useMemo(() => {
    try {
      return Object.keys(otherReqData ?? {})
        .filter((dateStr) => {
          // New format: each date maps to an array of detail objects
          const data = excludeOwnRequest(otherReqData[dateStr])
          return Array.isArray(data) && data.length > 0
        })
        .map((dateStr) => {
          const parts = dateStr.split('-')
          if (parts.length !== 3) return ''
          const [year, month, day] = parts
          return `${month}/${day}/${year}`
        })
        .filter(Boolean)
    } catch (error) {
      console.error('Error converting request dates:', error)
      return []
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otherReqData, excludeRequestId])

  // Transform API data to tooltip format - handle array-based response
  const otherRequestDetailsForTooltip = useMemo(() => {
    const result: Record<string, TooltipEntry> = {}

    try {
      if (!otherReqData || Object.keys(otherReqData).length === 0) {
        return result
      }

      Object.entries(otherReqData).forEach(
        ([dateKey, detailsArray]: [string, any]) => {
          try {
            const tooltipEntry = processDateEntry(
              dateKey,
              excludeOwnRequest(detailsArray),
              includeStatus
            )
            if (tooltipEntry) {
              result[dateKey] = tooltipEntry
            }
          } catch (error) {
            console.error(
              `Error processing date ${dateKey} in otherRequestDetailsForTooltip:`,
              error
            )
          }
        }
      )

      return result
    } catch (error) {
      console.error('Error transforming request details:', error)
      return result
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otherReqData, includeStatus, excludeRequestId])

  // Per-date breakdown for the "Hours in Other Requests" column. "Multi"
  // collapses allocations WITHIN a single existing request (matching
  // legacy) — it never merges separate requests on the same date together.
  const otherRequestHoursByDate = useMemo(() => {
    const result: Record<string, OtherRequestBreakdownEntry[]> = {}
    Object.entries(otherReqData ?? {}).forEach(([dateKey, detailsArray]) => {
      const parts = dateKey.split('-')
      if (parts.length !== 3) return
      const [year, month, day] = parts
      const mmddyyyy = `${month}/${day}/${year}`
      const otherRequestsOnly =
        excludeRequestId === undefined ||
        excludeRequestId === null ||
        excludeRequestId === ''
          ? detailsArray
          : (detailsArray ?? []).filter(
              (detail) => String(detail?.toReqId ?? '') !== String(excludeRequestId)
            )
      result[mmddyyyy] =
        Array.isArray(otherRequestsOnly) && otherRequestsOnly.length > 0
          ? toPerRequestBreakdownEntries(otherRequestsOnly)
          : []
    })
    return result
  }, [otherReqData, excludeRequestId])

  // Handle month/year change from calendar navigation
  const handleMonthYearChange = (month: number, year: number) => {
    if (Number.isNaN(month) || Number.isNaN(year)) return
    setViewedMonth(month)
    setViewedYear(year)
  }

  return {
    otherRequestDates,
    otherRequestDetailsForTooltip,
    otherRequestHoursByDate,
    handleMonthYearChange,
  }
}
